import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/service-area-pages");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/service-area-pages")({
  head: () => ({
    meta: [
      {
        title: "Service Area & City Landing Pages: How to Do Them Right (2026)",
      },
      {
        name: "description",
        content:
          "The right way to build city + service pages for every Colorado town you serve — without the doorway-page tactics that get sites tanked by Google.",
      },
      {
        property: "og:title",
        content: "Service Area Pages Done Right (Colorado)",
      },
      {
        property: "og:description",
        content:
          "How to rank in every Colorado city you serve — without getting penalized.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/service-area-pages` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/service-area-pages" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="8 min read"
      title="Service area & city landing pages: how to do them right (and not get penalized)"
      intro="Service area pages are the single biggest organic SEO lever for Colorado service businesses. They&rsquo;re also the easiest way to get your site demoted if you do them wrong. Here&rsquo;s the right way."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Service area pages" },
      ]}
      path="/learn/service-area-pages"
      faqs={[
        { q: "Do service-area pages still work for SEO in 2026?", a: "Yes — when they're written for humans, with unique local content (landmarks, projects, testimonials from that city). Cookie-cutter pages with the city name swapped in get penalized." },
        { q: "How many service-area pages should I have?", a: "One per city you actively serve and want to rank in. Don't create pages for cities you can't service or have zero reviews from — Google notices the disconnect." },
        { q: "What should every service-area page include?", a: "A unique intro about that city, the specific services offered there, local reviews or projects, a clear map, driving directions or service radius, and a city-specific CTA." },
      ]}
    >
      <h2>Why these pages matter so much</h2>
      <p>
        Google ranks pages that closely match what was searched. A search for &ldquo;furnace repair
        Monument CO&rdquo; will almost always lose to a page literally titled &ldquo;Furnace
        Repair in Monument, CO&rdquo; — even if the broader site is better.
      </p>
      <p>
        Multiply by 5 services × 8 cities and you&rsquo;ve got 40 ranking opportunities. That
        single concept is how most Colorado home-service businesses 3× their organic leads.
      </p>

      <h2>The wrong way (Google calls these &ldquo;doorway pages&rdquo;)</h2>
      <ul>
        <li>Take one template, swap the city name 30 times, hit publish.</li>
        <li>Same content, same photos, same testimonials — just &ldquo;[City]&rdquo; replaced.</li>
        <li>List 50 cities you don&rsquo;t actually serve to fish for traffic.</li>
        <li>No internal links between the pages, no real neighborhood detail.</li>
      </ul>
      <p>
        Google&rsquo;s spam systems have specifically targeted this since 2015. The penalty is
        real, fast, and brutal — your whole site can drop, not just the spam pages.
      </p>

      <h2>The right way</h2>
      <ol>
        <li>
          <strong>One page per city + service combo</strong> — &ldquo;AC repair in Monument&rdquo;
          and &ldquo;Furnace repair in Monument&rdquo; are <em>two</em> pages, not one
          &ldquo;Monument&rdquo; page covering everything.
        </li>
        <li>
          <strong>Locally specific content</strong> — Real neighborhoods, real landmarks, real
          drive-time reality (&ldquo;most Monument calls in winter are about heat-pump performance
          at altitude&rdquo;). If you&rsquo;ve never done a job there, don&rsquo;t make a page for
          it.
        </li>
        <li>
          <strong>City-specific testimonials and job photos</strong> — At least one local proof
          point per page. Pull the city out of your CRM history.
        </li>
        <li>
          <strong>Local schema markup</strong> — Service + AreaServed schema with the actual city
          and ZIPs.
        </li>
        <li>
          <strong>Internal linking</strong> — Each city page links to the parent service page and
          to nearby city pages. Builds topical authority.
        </li>
        <li>
          <strong>Embedded GMB map of your service area</strong> — Reinforces relevance.
        </li>
      </ol>

      <h2>How long does each page need to be?</h2>
      <p>
        500–900 words of <em>genuinely different</em> content beats 2,000 words of fluff. Quality,
        not length.
      </p>

      <h2>How many pages should you build?</h2>
      <p>
        For most Colorado service businesses, the sweet spot is:
      </p>
      <ul>
        <li><strong>5–10 core service pages</strong> (one per service)</li>
        <li><strong>5–15 city pages</strong> (only for cities you actually work in)</li>
        <li><strong>15–40 city + service combo pages</strong> for your top combos</li>
      </ul>
      <p>
        You don&rsquo;t need to launch all 40 on day one. Build 8–10 to start, watch what ranks,
        expand from there.
      </p>

      <h2>Internal links matter</h2>
      <p>
        The page for &ldquo;AC repair in Monument&rdquo; should link to your main{" "}
        <Link to="/colorado-springs-web-design" className="font-bold text-brand-gold-deep underline">
          AC repair page
        </Link>{" "}
        (well — your equivalent), to nearby cities (&ldquo;Black Forest,&rdquo;
        &ldquo;Falcon&rdquo;), and to your{" "}
        <Link to="/colorado-springs-google-maps" className="font-bold text-brand-gold-deep underline">
          Google Maps profile
        </Link>
        . That cluster signals to Google that you&rsquo;re a real local authority on AC repair
        across that region.
      </p>

      <h2>The bigger context</h2>
      <p>
        Service area pages are step 2 of the broader{" "}
        <Link to="/learn/local-seo-guide" className="font-bold text-brand-gold-deep underline">
          local SEO playbook
        </Link>
        . Done correctly, they compound — each new page lifts the others. Done lazily, they sink
        the whole site. Worth doing right.
      </p>
    </ArticleLayout>
  );
}
